import { formatLocalDateString, parseLocalDateString } from './day-type-override.utils';
export const FINISH_DAY_STREAK_MIN = 2;
export const FINISH_DAY_STREAK_SECONDARY_MAX = 2;
const STREAK_LABELS = {
    mealMapping: 'meal mapping',
    weighIn: 'weigh-in',
    exercise: 'exercise',
};
/**
 * Count consecutive active days ending at `endDate` (inclusive), walking backward.
 * `activeDates` is a set of yyyy-MM-dd strings that count for the habit.
 */
export function countConsecutiveActiveDays(endDate, activeDates) {
    const end = parseLocalDateString(endDate);
    if (!end) {
        return 0;
    }
    const active = activeDates instanceof Set
        ? activeDates
        : new Set(Array.from(activeDates));
    let count = 0;
    const cursor = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    while (true) {
        const key = formatLocalDateString(cursor);
        if (!active.has(key)) {
            break;
        }
        count += 1;
        cursor.setDate(cursor.getDate() - 1);
    }
    return count;
}
/**
 * Hero = meal mapping if ≥ 2; else highest among weigh-in / exercise (≥ 2).
 * Ties among non-meal: weigh-in wins over exercise.
 */
export function pickFinishDayStreakHero(streaks) {
    if (streaks.mealMapping >= FINISH_DAY_STREAK_MIN) {
        return { kind: 'mealMapping', count: streaks.mealMapping };
    }
    const candidates = [];
    if (streaks.weighIn >= FINISH_DAY_STREAK_MIN) {
        candidates.push({ kind: 'weighIn', count: streaks.weighIn });
    }
    if (streaks.exercise >= FINISH_DAY_STREAK_MIN) {
        candidates.push({ kind: 'exercise', count: streaks.exercise });
    }
    if (candidates.length === 0) {
        return null;
    }
    candidates.sort((a, b) => {
        if (b.count !== a.count) {
            return b.count - a.count;
        }
        // weighIn before exercise on tie
        if (a.kind === 'weighIn') {
            return -1;
        }
        if (b.kind === 'weighIn') {
            return 1;
        }
        return 0;
    });
    return candidates[0];
}
export function pickFinishDayStreakSecondary(streaks, hero) {
    const kinds = [
        'mealMapping',
        'weighIn',
        'exercise',
    ];
    const extras = kinds
        .filter(kind => hero == null || kind !== hero.kind)
        .map(kind => ({ kind, count: streaks[kind] }))
        .filter(s => s.count >= FINISH_DAY_STREAK_MIN)
        .sort((a, b) => b.count - a.count)
        .slice(0, FINISH_DAY_STREAK_SECONDARY_MAX);
    return extras;
}
export function buildFinishDayStreaksResult(args) {
    const streaks = {
        mealMapping: countConsecutiveActiveDays(args.date, args.mealDates),
        weighIn: countConsecutiveActiveDays(args.date, args.weighInDates),
        exercise: countConsecutiveActiveDays(args.date, args.exerciseDates),
    };
    const hero = pickFinishDayStreakHero(streaks);
    const secondary = pickFinishDayStreakSecondary(streaks, hero);
    return {
        date: args.date,
        streaks,
        hero,
        secondary,
    };
}
export function formatFinishDayStreakLabel(kind, count) {
    return `${count}-day ${STREAK_LABELS[kind]} streak`;
}
/** True when biometrics measurements count as a weigh-in day. */
export function measurementsHaveWeighIn(measurements) {
    if (!measurements) {
        return false;
    }
    const w = Number(measurements.weight);
    return Number.isFinite(w) && w > 0;
}
/** True when biometrics measurements count as an exercise day. */
export function measurementsHaveExercise(measurements) {
    if (!measurements) {
        return false;
    }
    const minutes = Number(measurements.exerciseMinutes);
    if (Number.isFinite(minutes) && minutes > 0) {
        return true;
    }
    const burn = Number(measurements.exerciseBurnKcal);
    if (Number.isFinite(burn) && burn > 0) {
        return true;
    }
    if (typeof measurements.exerciseType === 'string' &&
        measurements.exerciseType.trim().length > 0) {
        return true;
    }
    return false;
}
